import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, BarChart3, User, Plus, Settings } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useManuals } from '../../contexts/ManualsContext';

export const AdminPanel = () => {
  const { darkMode } = useTheme();
  const { userRole, adminStats, loadAdminStats } = useAuth();
  const {
    showAdminPanel,
    setShowAdminPanel,
    setIsAddingManual,
    setIsManagingUsers,
    passwordResetRequests
  } = useManuals();

  useEffect(() => {
    if (showAdminPanel && userRole === 'admin') {
      loadAdminStats();
    }
  }, [showAdminPanel, userRole, loadAdminStats]);

  if (!showAdminPanel || userRole !== 'admin') return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`${darkMode ? 'bg-blue-900/30 border-t border-blue-800' : 'bg-blue-50 border-t border-blue-100'} p-4`}
    >
      <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className={`font-bold text-lg ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>Панель управления</h2>
          <p className={`text-sm ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>Управление мануалами и пользователями</p>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <button
            onClick={() => {
              setIsAddingManual(true);
              if (window.innerWidth < 768) setShowAdminPanel(false);
            }}
            className={`flex items-center justify-center space-x-2 px-4 py-2.5 rounded-lg transition-colors touch-target w-full sm:w-auto ${
              darkMode ? 'bg-blue-700 hover:bg-blue-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            <Plus className="w-4 h-4" />
            <span className="text-sm">Добавить мануал</span>
          </button>
          <button
            onClick={() => {
              setIsManagingUsers(true);
              if (window.innerWidth < 768) setShowAdminPanel(false);
            }}
            className={`flex items-center justify-center space-x-2 px-4 py-2.5 rounded-lg transition-colors touch-target w-full sm:w-auto relative ${
              darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            <Users className="w-4 h-4" />
            <span className="text-sm">Пользователи</span>
            {passwordResetRequests?.some(req => req.status === 'pending') && (
              <span className={`absolute -top-2 -right-2 text-xs w-5 h-5 rounded-full flex items-center justify-center ${
                darkMode ? 'bg-red-600 text-red-100' : 'bg-red-500 text-white'
              }`}>
                {passwordResetRequests.filter(req => req.status === 'pending').length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Статистика */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {/* Онлайн пользователи */}
        <div className={`p-3 md:p-4 rounded-lg ${
          darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs md:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Онлайн</p>
              <p className={`text-xl md:text-2xl font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                {adminStats?.onlineUsers || 0}
              </p>
            </div>
            <Users className={`w-6 h-6 md:w-8 md:h-8 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
          </div>
          {adminStats?.onlineUsersList?.length > 0 && (
            <div className={`mt-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <p className="truncate">Активные: {adminStats.onlineUsersList.slice(0, 2).map(u => u.name).join(', ')}</p>
              {adminStats.onlineUsersList.length > 2 && (
                <p>+{adminStats.onlineUsersList.length - 2} еще</p>
              )}
            </div>
          )}
        </div>

        {/* Актуальные проекты */}
        <div className={`p-3 md:p-4 rounded-lg ${
          darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs md:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Проектов</p>
              <p className={`text-xl md:text-2xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                {adminStats?.activeProjects || 0}
              </p>
            </div>
            <BarChart3 className={`w-6 h-6 md:w-8 md:h-8 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
          </div>
          {adminStats?.activeProjectsList?.length > 0 && (
            <div className={`mt-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <p className="truncate">{adminStats.activeProjectsList[0]?.project}</p>
              {adminStats.activeProjectsList.length > 1 && (
                <p>+{adminStats.activeProjectsList.length - 1} еще</p>
              )}
            </div>
          )}
        </div>

        {/* Всего пользователей */}
        <div className={`p-3 md:p-4 rounded-lg ${
          darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs md:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Пользователей</p>
              <p className={`text-xl md:text-2xl font-bold ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                {adminStats?.totalUsers || 0}
              </p>
            </div>
            <User className={`w-6 h-6 md:w-8 md:h-8 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
          </div>
          <div className={`mt-2 grid grid-cols-2 gap-2 text-xs`}>
            <div>
              <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>TimeSheet:</p>
              <p className={`font-bold ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                {adminStats?.totalTimesheetReports || 0}
              </p>
            </div>
            <div>
              <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Переработки:</p>
              <p className={`font-bold ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
                {adminStats?.totalOvertimeReports || 0}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};